import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  GiftIcon,
  ShoppingCartIcon,
  TagIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const BundlesPage = () => {
  const { user } = useAuth();
  const { loadCart } = useCart();
  const navigate = useNavigate();
  
  const [bundles, setBundles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchBundles();
    fetchCategories();
  }, [search, selectedCategory, showFeaturedOnly, sortBy, sortOrder]);

  const fetchBundles = async () => {
    try {
      setLoading(true);
      const params = {
        search,
        sort: sortBy,
        order: sortOrder,
        limit: 100,
        customer_type: 'b2c' // Only show B2C bundles to public customers
      };
      
      if (selectedCategory) params.category = selectedCategory;
      if (showFeaturedOnly) params.featured = 'true';
      
      const response = await axios.get('/api/bundles', { params });
      setBundles(response.data.bundles || []);
    } catch (error) {
      setError('שגיאה בטעינת החבילות');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data.categories || response.data.allCategories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddToCart = async (bundle) => {
    try {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId') || `session-${Date.now()}`;
      
      if (!localStorage.getItem('sessionId')) {
        localStorage.setItem('sessionId', sessionId);
      }
      
      await axios.post('/api/cart/bundle', 
        { 
          bundle_id: bundle.id, 
          quantity: 1 
        },
        {
          headers: {
            'x-session-id': sessionId,
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );
      
      // Reload cart to update count
      await loadCart();
      
      toast.success('החבילה נוספה לעגלה בהצלחה! 🎁');
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בהוספת החבילה לעגלה');
    }
  };

  const calculateSavings = (regularPrice, bundlePrice) => {
    if (!regularPrice || !bundlePrice) return 0;
    return Math.round(((regularPrice - bundlePrice) / regularPrice) * 100);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <GiftIcon className="h-16 w-16" />
            </div>
            <h1 className="text-4xl font-bold mb-4">חבילות מבצע</h1>
            <p className="text-xl text-primary-100">
              חסכו כסף עם החבילות המיוחדות שלנו - מוצרים איכותיים במחיר משתלם!
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                חיפוש
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="חפש חבילות..."
                  className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                קטגוריה
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">כל הקטגוריות</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                מיון
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="created_at">חדש ביותר</option>
                <option value="bundle_price">מחיר נמוך לגבוה</option>
                <option value="discount_percentage">הנחה גבוהה ביותר</option>
                <option value="sales_count">הכי נמכר</option>
              </select>
            </div>
          </div>

          {/* Featured Filter */}
          <div className="mt-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showFeaturedOnly}
                onChange={(e) => setShowFeaturedOnly(e.target.checked)}
                className="rounded text-primary-600 focus:ring-primary-500 h-4 w-4"
              />
              <span className="mr-2 text-sm text-gray-700">הצג רק חבילות מומלצות</span>
              <SparklesIcon className="h-4 w-4 text-yellow-500 mr-1" />
            </label>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : bundles.length === 0 ? (
          <div className="text-center py-12">
            <GiftIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">אין חבילות זמינות</h3>
            <p className="text-gray-500">נסה לשנות את הפילטרים או חזור מאוחר יותר</p>
          </div>
        ) : (
          /* Bundles Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bundles.map((bundle) => {
              const savings = calculateSavings(bundle.regular_price, bundle.bundle_price);
              
              return (
                <div
                  key={bundle.id}
                  className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  {/* Bundle Image */}
                  <div className="relative h-64 bg-gray-200">
                    {bundle.bundle_image || bundle.images?.[0] ? (
                      <img
                        src={`/api${bundle.bundle_image || bundle.images[0]}`}
                        alt={bundle.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <GiftIcon className="h-20 w-20 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Badges */}
                    <div className="absolute top-3 right-3 flex flex-col gap-2">
                      {/* Status Badge */}
                      {bundle.status && bundle.status !== 'active' && (
                        <span className={`px-3 py-1 text-white text-sm font-bold rounded-full shadow-lg ${
                          bundle.status === 'not_started' ? 'bg-blue-500' : 
                          bundle.status === 'expired' ? 'bg-red-500' : 'bg-gray-500'
                        }`}>
                          {bundle.status_message || bundle.status}
                        </span>
                      )}
                      {bundle.is_featured && (
                        <span className="px-3 py-1 bg-yellow-500 text-white text-sm font-bold rounded-full shadow-lg flex items-center">
                          <SparklesIcon className="h-4 w-4 ml-1" />
                          מומלץ
                        </span>
                      )}
                      {savings > 0 && (
                        <span className="px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full shadow-lg">
                          חסכון {savings}%
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bundle Details */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                      {bundle.title}
                    </h3>
                    
                    {bundle.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {bundle.description}
                      </p>
                    )}

                    {/* Products Count */}
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <TagIcon className="h-4 w-4 ml-1" />
                      <span>{bundle.items?.length || 0} מוצרים בחבילה</span>
                    </div>

                    {/* Pricing */}
                    <div className="border-t pt-4 mb-4">
                      {bundle.regular_price && (
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-500">מחיר רגיל:</span>
                          <span className="text-sm text-gray-500 line-through">
                            ₪{parseFloat(bundle.regular_price).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-semibold text-gray-700">מחיר חבילה:</span>
                        <span className="text-2xl font-bold text-primary-600">
                          ₪{parseFloat(bundle.bundle_price).toFixed(2)}
                        </span>
                      </div>
                      {bundle.regular_price && (
                        <div className="text-sm text-green-600 text-left mt-1">
                          חוסכים ₪{(parseFloat(bundle.regular_price) - parseFloat(bundle.bundle_price)).toFixed(2)}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Link
                        to={`/bundles/${bundle.id}`}
                        className="flex-1 btn btn-outline text-center"
                      >
                        פרטים נוספים
                      </Link>
                      <button
                        onClick={() => handleAddToCart(bundle)}
                        className="flex-1 btn btn-primary flex items-center justify-center"
                      >
                        <ShoppingCartIcon className="h-5 w-5 ml-2" />
                        הוסף לעגלה
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BundlesPage;
